import createDebug from 'debug';
import { isUndefined } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'universal-cookie';

import useFeature from '@swf/core//hooks/useFeature';
import {
  Brand,
  CRMZone,
  DebugKey,
  FeatureFlag,
  ResolverTag,
  Site,
  TokenType
} from '@swf/core/enums';
import { StatusCode } from '@swf/core/enums/statusCode';
import useFromEvent from '@swf/core/hooks/useFromEvent';
import useNavigate from '@swf/core/hooks/useNavigate';
import useOpenWindow from '@swf/core/hooks/useOpenWindow';
import useRequest from '@swf/core/hooks/useRequest';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { AccountCurrency } from '@swf/core/types/Account';
import CookieNames from '@swf/core/types/Cookies';
import { customEvent } from '@swf/core/utils';

enum RollType {
  InstantBonuses,
  FreeSpinBonuses,
  GameTickets
}

const getAccountPath = (section: string, depositLimitPath: string) => {
  switch (section) {
    case 'sports/history':
      return '/account/history/playing-history-audit';
    case '/sports/preferences':
      return '/account/sports/preferences';
    case '/responsible/restrict/sports':
      return '/account/responsible/restrict-sports-stake-limit';
    case '/responsible/restrict/deposit':
      return depositLimitPath;
    case '/history/aams':
      return '/account/history/aams-history';
    case 'history/account/statement':
      return '/account/history/account-history-statement';
    case 'history/handHistory':
      return '/account/history/hand-history';
    default:
      return '/account';
  }
};

const getBonusPath = (rollType: RollType) => {
  switch (rollType) {
    case RollType.InstantBonuses:
      return 'instant-bonuses';
    case RollType.FreeSpinBonuses:
      return 'free-spin-bonuses';
    case RollType.GameTickets:
      return 'game-tickets';
  }
};

const getCashierPath = (detail: {
  cashierHome?: boolean;
  balance?: boolean;
  preferredCurrency?: boolean;
}) => {
  if (detail.cashierHome) {
    return '/account/cashier/home';
  } else if (detail.balance) {
    return '/account/cashier/balances';
  } else if (detail.preferredCurrency) {
    return '/account/cashier/preferred-currency';
  }
};

const cookies = new Cookies();
const debug = createDebug(DebugKey.Bridge);

const LegacyRAMProvider: React.FC = ({ children }) => {
  const request = useRequest();
  const navigate = useNavigate();
  const isMounted = useRef(false);
  const fromEvent = useFromEvent();
  const { formatNumber } = useIntl();
  const siteId = useSelector(select.site.getId);
  const brand = useSelector(select.site.getBrand);
  const useRam1 = useFeature(FeatureFlag.UseRam1);
  const DEPanic = useFeature(FeatureFlag.DEPanic);
  const session = useSelector(select.session.get);
  const account = useSelector(select.account.get);
  const balances = useSelector(select.account.getBalances);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const depositLimitPath = useSelector(select.account.getDepositLimitPath);
  const totalBalanceInUsd = useSelector(select.account.getTotalBalanceInUsd);
  const resolverOptions = {
    tokenType: isAuthenticated ? TokenType.CustomerSupport : TokenType.Invalid
  };
  const openVerifyAml = useOpenWindow({ resolverTag: ResolverTag.VerifyMeAML, resolverOptions });
  const openLiveChat = useOpenWindow({ resolverTag: ResolverTag.LiveChatSupport, resolverOptions });
  const isLegacySportEnabled = [Brand.BetStars, Brand.FoxBet].includes(brand) && !useRam1;

  const { auth, modal } = useDispatch<Dispatch>();

  const eventListener = useCallback(
    (eventName: string, handler: (e: CustomEvent) => void) =>
      fromEvent(document, eventName).subscribe((e) => {
        const event = e as CustomEvent;
        debug('event: %o', event);
        handler(event);
      }),
    [fromEvent]
  );

  const {
    handleChangeLanguage,
    handleSendNotification,
    logout,
    openAccount,
    openBonuses,
    openCashier,
    openContactSupport,
    openEmailValidation,
    openIce,
    openLeaderBoards,
    openLicenseMigration,
    openLogin,
    openPlayMoneyCashier,
    openRestrictDepositLimit,
    openRMF,
    openSignup,
    openStarsRewards,
    openVIPStore
  } = useMemo(
    () => ({
      handleSendNotification: ({ detail }: CustomEvent) => {
        if (detail.notificationType === 10) openRestrictDepositLimit();
      },
      handleChangeLanguage: ({ detail }: CustomEvent) => window.location.assign(detail),
      logout: () => auth.logout({}),
      openAccount: ({ detail }: CustomEvent) => {
        const path = getAccountPath(detail.section, depositLimitPath);
        modal.enqueue({ path });
      },
      openBonuses: ({ detail }: CustomEvent) => {
        const path = getBonusPath(detail.roolType);
        path && modal.unshift({ path: `/account/my-stars/bonus/${path}` });
      },
      openCashier: ({ detail }: CustomEvent) => {
        const path = getCashierPath(detail);
        path ? modal.enqueue({ path }) : navigate('/deposit');
      },
      openContactSupport: () => modal.replace([{ path: '/account/help/contact-support' }]),
      openEmailValidation: () => modal.enqueue({ path: '/notifications/confirm-email' }),
      openIce: () => modal.enqueue({ path: '/account/ice/challenges' }),
      openLeaderBoards: () => modal.enqueue({ path: '/account/ice/leaderboards' }),
      openLicenseMigration: () => modal.enqueue({ path: '/account/migration' }),
      openLogin: () => modal.enqueue({ path: '/auth/login' }),
      openPlayMoneyCashier: () => modal.enqueue({ path: '/account/cashier/paywall' }),
      openRestrictDepositLimit: () => modal.enqueue({ path: depositLimitPath }),
      openRMF: () => modal.enqueue({ path: '/account/cashier/rmf' }),
      openSignup: () => navigate('/signup'),
      openVIPStore: () => modal.enqueue({ path: '/account/my-stars/rewards-store' }),
      openStarsRewards: () =>
        modal.enqueue({
          path: `/stars-crm/${CRMZone.STARS_REWARDS_OPT_IN}`,
          locationState: { icon: 'light', componentType: 'Single' }
        })
    }),
    [auth, depositLimitPath, modal, navigate]
  );

  useEffect(() => {
    if (isLegacySportEnabled && !isUndefined(DEPanic)) {
      eventListener('openselfexcludese24', () => {
        if (siteId === Site.SE) {
          modal.enqueue({ path: '/notifications/panic-button' });
          return;
        }
        if (DEPanic) {
          request({
            service: 'responsiblegaming.panicbutton'
          }).subscribe(
            () => modal.enqueue({ path: '/notifications/cooling-off-period' }),
            ({ headers: { statusCode } }) => {
              if (statusCode === StatusCode.ALREADY_SELF_EXCLUDED_ERROR) {
                modal.enqueue({ path: '/notifications/exclusion-info-vegas' });
              }
            }
          );
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DEPanic, eventListener, isLegacySportEnabled, modal, siteId]);

  useEffect(() => {
    if (isLegacySportEnabled) {
      eventListener('balanceInfoRequest', () => {
        if (balances?.length) {
          const { chips, currency } = balances?.[0] as AccountCurrency;
          const data = {
            balance: chips,
            balancestr: `${formatNumber(chips / 100, { minimumFractionDigits: 2 })} ${currency}`,
            currency,
            totalbalanceusd: totalBalanceInUsd
          };

          customEvent({ tag: 'balanceInfo', data });
        }
      });
    }
  }, [balances, eventListener, formatNumber, isLegacySportEnabled, totalBalanceInUsd]);

  useEffect(() => {
    if (isLegacySportEnabled) {
      if (isAuthenticated) {
        const data = {
          ...session,
          account,
          SBSG: cookies.get(CookieNames.SIGNATURE),
          SBTK: cookies.get(CookieNames.TOKEN),
          WBID: cookies.get(CookieNames.WEB_ID),
          XIID: cookies.get(CookieNames.INSTALL_ID)
        };
        customEvent({ tag: 'loggedIn', data });
      }
      if (!isAuthenticated && isMounted.current) {
        customEvent({ tag: 'loggedOut' });
      }
      isMounted.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (isLegacySportEnabled) {
      eventListener('contactSupportRequest', openContactSupport);
      eventListener('licenseMigrationRequest', openLicenseMigration);
      eventListener('logInRequest', openLogin);
      eventListener('logOutRequest', logout);
      eventListener('openAccountRequest', openAccount);
      eventListener('openBonusManager', openBonuses);
      eventListener('openCreateAccountRequest', openSignup);
      eventListener('openEmailValidation', openEmailValidation);
      eventListener('openFCRequest', openPlayMoneyCashier);
      eventListener('openIceRequest', openIce);
      eventListener('openLeaderBoardsRequest', openLeaderBoards);
      eventListener('openLiveChat', openLiveChat);
      eventListener('openPayWallRequest', openPlayMoneyCashier);
      eventListener('openRestrictDepositLimitRequest', openRestrictDepositLimit);
      eventListener('openRmfRequest', openRMF);
      eventListener('openVerifyAml', openVerifyAml);
      eventListener('openVIPStoreRequest', openVIPStore);
      eventListener('openWCRequest', openCashier);
      eventListener('ramChangeLang', handleChangeLanguage);
      eventListener('sendNotification', handleSendNotification);
      eventListener('srwrdsStart', openStarsRewards);
    }
  }, [
    eventListener,
    handleChangeLanguage,
    handleSendNotification,
    isLegacySportEnabled,
    logout,
    openAccount,
    openBonuses,
    openCashier,
    openContactSupport,
    openEmailValidation,
    openIce,
    openLeaderBoards,
    openLicenseMigration,
    openLiveChat,
    openLogin,
    openPlayMoneyCashier,
    openRestrictDepositLimit,
    openRMF,
    openSignup,
    openStarsRewards,
    openVerifyAml,
    openVIPStore
  ]);

  return React.createElement(React.Fragment, null, children);
};

export default LegacyRAMProvider;
